import "simplebar-react/dist/simplebar.min.css";

import React, {useEffect, useMemo, useState} from "react";
import {Listbox, ListboxOptions} from "headlessui-v2";
import {twJoin} from "tailwind-merge";
import useProductInfo from "../../context/ProductInfoProvider";
import {ProductSelectionGroup, ProductSelectionOption} from "@/data/entities/cart";
import ProductOptionButton from "@widgets/pos/order/menu/product/options/option/ProductOptionButton";
import SimpleBar from "simplebar-react";
import ProductOptionListHeader from "./ProductOptionListHeader";
import ProductOptionListItem from "./ProductOptionListItem";
import {listBoxClassNames} from "@widgets/input/listbox/listbox";
import {comboboxOptionsClassName} from "@widgets/input/combobox/combobox";

type Props = {
    displayName: string,
    group: ProductSelectionGroup,
};

const ProductOptionList = ({displayName, group}: Props) => {
    const {setProductSelection} = useProductInfo();
    const [notifyMaxReached, setNotifyMaxReached] = useState(false);
    const selectedValues: ProductSelectionOption[] = group.options.filter(o => o.selected)
    const hasReachedLimit: boolean | null = useMemo(() => {
        return group.canSelectMultiple
            && group.maxOptions > 0
            && selectedValues.length === group.maxOptions
    }, [group.canSelectMultiple, group.maxOptions, selectedValues]);

    // Auto. stop notify max. reached
    useEffect(() => {
        if (!notifyMaxReached) {
            return;
        }

        const timeout = setTimeout(() => setNotifyMaxReached(false), 400);
        return () => clearTimeout(timeout);
    }, [notifyMaxReached, setNotifyMaxReached]);

    return (
        <Listbox
            value={group.canSelectMultiple ? selectedValues : (selectedValues.length > 0 ? selectedValues [0] : [])}
            onChange={(value: ProductSelectionOption | ProductSelectionOption[]) => {
                if (hasReachedLimit && Array.isArray(value) && value.length > group.maxOptions) {
                    setNotifyMaxReached(true);
                    return;
                }

                const selectedOptionExternalIds: string[] = Array.isArray(value)
                    ? value.map(v => v.optionExternalId)
                    : [value.optionExternalId];

                setProductSelection(prev => ({
                    ...prev,
                    groups: prev.groups.map((g: ProductSelectionGroup) => ({
                        ...g,
                        ...(g.groupExternalId === group.groupExternalId ? {
                            options: g.options.map((o: ProductSelectionOption) => ({
                                ...o,
                                selected: selectedOptionExternalIds.includes(o.optionExternalId),
                            })),
                        } : {}),
                    }))
                }));
            }}
            multiple={group.canSelectMultiple}
        >
            <ProductOptionButton selected={selectedValues}/>

            <ListboxOptions
                anchor="bottom"
                transition
                className={twJoin(
                    listBoxClassNames,
                    comboboxOptionsClassName,
                    "[--anchor-max-height:20000px] outline-0"
                )}
            >
                <SimpleBar
                    className={"relative max-h-[300px] w-full"}
                    forceVisible="y"
                    autoHide={false}
                >
                    <ProductOptionListHeader
                        displayName={displayName}
                        group={group}
                        selectedValues={selectedValues}
                        notifyMaxReached={notifyMaxReached}
                    />

                    {group.options.map((option: ProductSelectionOption) => (
                        <ProductOptionListItem
                            key={option.optionExternalId}
                            option={option}
                            canSelectMultiple={group.canSelectMultiple}
                        />
                    ))}
                </SimpleBar>
            </ListboxOptions>
        </Listbox>
    );
};

export default ProductOptionList;